<template>
  <div>
    <v-navigation-drawer
      ref="customiseDemandsAndNeedsAdvised"
      v-model="valueLocal"
      app
      right
      temporary
      class="v-navigation-drawer--width-max"
    >
      <div class="drawer-container d-flex flex-column justify-space-between">
        <div
          class="drawer-header d-flex align-center justify-space-between pa-5"
        >
          <h3 class="font-24 font-weight-bold">Create SDN</h3>
          <v-btn icon @click="valueLocal = !valueLocal" class="btn-close">
            <v-icon size="32">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="drawer-main flex-grow-1 pa-2 pa-xl-3">
          <template>
            <v-container>
              <div class="pt-5">
                <!-- Error Alert -->
                <BaseAlert
                  v-if="isRequestError"
                  ref="error"
                  color="var(--red-100)"
                  class="mb-5 pa-4 border-sm border-solid border-error"
                >
                  <div class="d-flex error-message">
                    <div class="mr-3">
                      <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
                    </div>
                    <div class="primary--text">
                      <span class="font-weight-bold">Sorry!</span
                      >&nbsp;Something went wrong. Please try again.
                    </div>
                  </div>
                </BaseAlert>

                <!-- The Questions -->
                <div
                  v-for="section in sdnTemplate"
                  :key="section.id"
                  class="row"
                >
                  <div class="col">
                    <div class="row">
                      <div class="col px-6 pb-0">
                        <h4 class="font-20 mb-2">{{ section.title }}</h4>
                        <p class="ma-0">{{ section.description }}</p>
                      </div>
                    </div>
                    <div
                      v-for="(question, index) in section.questions"
                      :key="index"
                      class="row"
                    >
                      <div class="col">
                        <div class="d-flex flex-column flex-lg-row flex-wrap">
                          <div class="d-flex align-center col-12 col-lg-7">
                            {{ question.text }}
                          </div>
                          <div class="d-flex align-center col-12 col-lg-5">
                            <BaseToggleButton
                              :height="
                                $vuetify.breakpoint.lgAndUp ? '48' : '60'
                              "
                              v-model="question.value"
                            />
                          </div>
                        </div>
                        <!-- Sub questions -->
                        <template v-if="question.questions && question.value">
                          <div
                            v-for="(subQuestion, index) in question.questions"
                            :key="index"
                            class="row mt-3"
                          >
                            <div class="col">
                              <div
                                class="d-flex flex-column flex-lg-row flex-wrap"
                              >
                                <div
                                  class="d-flex align-center col-12 col-lg-7"
                                >
                                  {{ subQuestion.text }}
                                </div>
                                <div
                                  class="d-flex align-center col-12 col-lg-5"
                                >
                                  <BaseToggleButton
                                    :height="
                                      $vuetify.breakpoint.lgAndUp ? '48' : '60'
                                    "
                                    v-model="subQuestion.value"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mt-5">
                  <div class="col px-6 pb-0">
                    <ValidationObserver ref="observerFormSDN">
                      <v-form ref="submitForm">
                        <BaseTextarea
                          v-model="sdnTemplate[0].additionalText"
                          label="For the purposes of this insurance policy, are there any specific requirements you have?"
                          hideValidationIcon
                          :hideDetails="false"
                          :rules="{
                            max: {
                              length: 500,
                              errorMessageTemplate:
                                'SDN contains a statement that is more than 500 characters long, which is not allowed.',
                            },
                          }"
                        />
                      </v-form>
                    </ValidationObserver>
                  </div>
                </div>
              </div>

              <BaseAlert
                color="var(--red-100)"
                class="mt-1 mb-0 mb-7 mx-7 pa-4 border-sm border-solid border-error"
                v-if="invalidSaveSDNs"
              >
                <div class="d-flex flex-no-wrap">
                  <div class="mr-4 v-messages error--text">
                    <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
                  </div>
                  <div class="primary--text">
                    <span class="font-weight-bold">Sorry!</span>
                    There must be at least one completed statement to create a
                    statement of demands and needs.
                  </div>
                </div>
              </BaseAlert>
            </v-container>
          </template>
        </div>

        <div class="drawer-footer d-flex flex-column pa-5">
          <BaseButton
            text="PROCEED"
            height="68"
            block
            color="psorange"
            :disabled="invalidSaveSDNs"
            class="font-weight-bold font-20 rounded-pill black--text submit-btn"
            :loading="isProcessingSDN"
            loadingText="PROCEEDING"
            loadingIconColor="black"
            @click="saveSDNs()"
            type="submit"
          />
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { EventBus } from "@/common/eventbus.js";
import { dereference } from "@/common/transforms";
import { handleValidationError } from "@/common/utils";
import { sdnTemplateToSubmissionAdvised } from "@/common/sdn";
import { APPLICATION_MODULE } from "@/store/types/module-types";

import { ValidationObserver } from "vee-validate";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseTextarea from "@/components/BaseComponents/BaseTextarea.vue";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";

export default {
  components: {
    BaseAlert,
    BaseButton,
    BaseTextarea,
    BaseToggleButton,
    ValidationObserver,
  },
  props: {
    value: Boolean,
    sdnData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      sdnTemplate: dereference(this.sdnData),
      valueLocal: this.value,
      navDrawerContent: null,
      isRequestError: false,
      isProcessingSDN: false,
      invalidSaveSDNs: false,
    };
  },
  computed: {
    sdnSubmission() {
      return sdnTemplateToSubmissionAdvised(this.sdnTemplate);
    },
    suitabilityReasons() {
      return this.sdnSubmission?.Sections.find((section) => {
        return section.InterfaceKey === "SuitabilityReasons";
      });
    },
  },
  watch: {
    value(val) {
      this.valueLocal = val;
    },
    valueLocal(val) {
      this.$emit("input", val);

      // when the drawer opens/closes we set the scroll position to the top
      // the second arg is the target element
      // without this Vuetify will default to the page rather than the element
      this.$vuetify.goTo(0, { container: this.navDrawerContent });
    },
    "suitabilityReasons.Statements": function () {
      if (!this.isSuitabilityReasonsEmpty()) {
        this.invalidSaveSDNs = false;
      }
    },
  },
  created() {
    // Listen for validation errors globally
    EventBus.$on("validation-error", handleValidationError);
  },
  mounted() {
    // set which element we want to control scrolling
    this.navDrawerContent =
      this.$refs.customiseDemandsAndNeedsAdvised.$el.querySelector(
        "div.v-navigation-drawer__content"
      );
  },
  methods: {
    ...mapActions(APPLICATION_MODULE, ["handleSaveSDNs"]),
    isSuitabilityReasonsEmpty() {
      return !this.suitabilityReasons?.Statements?.length;
    },
    async saveSDNs() {
      const isValidForm = await this.$refs.observerFormSDN.validate();

      if (!isValidForm) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }

      if (this.isSuitabilityReasonsEmpty()) {
        this.invalidSaveSDNs = true;
        // scroll to alert area
        setTimeout(() => {
          EventBus.$emit("validation-error", "Validation error occurred");
        }, 100);
        return;
      }
      this.isRequestError = false;
      this.isProcessingSDN = true;

      this.handleSaveSDNs(this.sdnSubmission)
        .then(() => {
          this.isProcessingSDN = false;
          this.valueLocal = false;
          this.invalidSaveSDNs = false;
          this.$emit("refreshDocs");
          this.sdnTemplate = dereference(this.sdnData);
        })
        .catch((err) => {
          console.error(err);
          this.isRequestError = true;
          this.isProcessingSDN = false;
          this.$vuetify.goTo(0, { container: this.navDrawerContent });
          this.sdnTemplate = dereference(this.sdnData);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/AppComponents/_DrawerCustomizeCover.scss";
.submit-btn.v-btn.v-btn--block.v-btn--disabled.v-btn--has-bg {
  background-color: $ps-orange !important;
  color: $black-2 !important;
  opacity: 0.5 !important;
  border: none !important;
}
</style>
